import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import TableCardView from "./ui/tableCardView";
import { GetUsers, SearchUsers, GetDropdownData, GetStateCityDropdown, GetCommunityStatistics, GetCustomerSupportAdmins, getMembersOverview, GetUserMatchStats } from "./apis/get";
import { PatchAssignUserInstruction, PatchUpdatePhoneNumber, PatchUpdateUserCommunityCaste } from "./apis/patch";
import { PostAdminService, PostGenerateAboutMe, PostTriggerServices } from "./apis/post";
import LoaderService from "../services/LoaderService";
import toast from "react-hot-toast";
import SimpleMessageModal from "./SimpleNotification";

const UserAvatar = ({ name, photo }) => {
  const [loaded, setLoaded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const initial = name ? name.trim()[0].toUpperCase() : 'U';

  return (
    <>
      <div
        className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-[10px] font-black text-slate-400 border border-slate-700 overflow-hidden relative cursor-zoom-in hover:border-blue-500 transition-colors"
        onMouseEnter={() => setShowPreview(true)}
        onMouseLeave={() => setShowPreview(false)}
      >
        <span className={loaded ? 'hidden' : ''}>{initial}</span>
        {photo && (
          <img
            src={photo}
            alt={name}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setLoaded(true)}
          />
        )}
      </div>

      {showPreview && photo && createPortal(
        <div className="fixed top-1/2 left-[60%] -translate-x-1/2 -translate-y-1/2 z-[99999] w-[500px] h-[500px] rounded-2xl border-4 border-slate-900 shadow-[0_0_100px_rgba(0,0,0,1)] bg-black/95 p-2 pointer-events-none animate-in fade-in zoom-in-95 duration-200">
          <img
            src={photo}
            className="w-full h-full object-contain rounded-xl"
            alt={name}
          />
        </div>,
        document.body
      )}
    </>
  );
};

const AboutMeProgressToast = ({ progress, userName, status = "loading" }) => {
  const normalizedProgress = Math.max(0, Math.min(100, Math.round(progress)));
  const title =
    status === "success"
      ? "About Me generated successfully"
      : "About Me is generating, please wait...";
  const barClasses =
    status === "success"
      ? "from-emerald-500 to-emerald-300"
      : "from-amber-500 to-orange-400";

  return (
    <div className="w-[320px] rounded-2xl border border-slate-700/70 bg-slate-950/95 p-4 shadow-2xl backdrop-blur-xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.2em] text-white">
            {title}
          </div>
          <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            {userName ? `User: ${userName}` : "Preparing profile summary"}
          </div>
        </div>
        <div className="min-w-[44px] text-right text-sm font-black text-amber-400">
          {normalizedProgress}%
        </div>
      </div>

      <div className="mt-3 h-2 overflow-hidden rounded-full border border-slate-800 bg-slate-900">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${barClasses} transition-all duration-300`}
          style={{ width: `${normalizedProgress}%` }}
        />
      </div>

      <div className="mt-2 text-[10px] font-medium text-slate-500">
        {status === "success"
          ? "The request is complete."
          : normalizedProgress >= 90
            ? "Finalizing response from the API..."
            : "Building the progress while the API works in the background."}
      </div>
    </div>
  );
};

const UserActionMenu = ({ row, onRun, onGenerateAboutMe }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const isComplete = (row.progress || 0) >= 100;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAction = (event, action) => {
    event.stopPropagation();
    if (!isComplete) return;

    setIsOpen(false);
    action();
  };

  return (
    <div className="relative inline-flex" ref={menuRef}>
      <button
        onClick={(event) => {
          event.stopPropagation();
          if (!isComplete) return;
          setIsOpen((prev) => !prev);
        }}
        disabled={!isComplete}
        className={`px-4 py-1.5 rounded-lg inline-flex items-center gap-2 text-[10px] font-black uppercase border transition-all ${
          isComplete
            ? "bg-purple-500/10 text-purple-400 border-purple-500/20 hover:bg-purple-500/20 hover:border-purple-500/30 hover:shadow-[0_0_15px_rgba(168,85,247,0.2)] active:scale-95"
            : "bg-slate-800 text-slate-600 border-slate-700 cursor-not-allowed opacity-50"
        }`}
        title={
          isComplete
            ? "Open user actions"
            : "Profile must be 100% complete to use actions"
        }
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v.01M12 12v.01M12 18v.01M12 6a1 1 0 100 2 1 1 0 000-2zm0 6a1 1 0 100 2 1 1 0 000-2zm0 6a1 1 0 100 2 1 1 0 000-2z" />
        </svg>
        Actions
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19 9-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full z-[150] mt-2 min-w-[170px] overflow-hidden rounded-xl border border-slate-700/80 bg-slate-950 shadow-[0_20px_60px_rgba(0,0,0,0.75)]"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            onClick={(event) => handleAction(event, () => onRun(row.id))}
            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[10px] font-black uppercase tracking-wider text-slate-200 transition-colors hover:bg-white/5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Run
          </button>

          <button
            onClick={(event) => handleAction(event, () => onGenerateAboutMe(row))}
            className="flex w-full items-center gap-2 border-t border-slate-800 px-3 py-2.5 text-left text-[10px] font-black uppercase tracking-wider text-slate-200 transition-colors hover:bg-white/5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
            </svg>
            About Me
          </button>
        </div>
      )}
    </div>
  );
};
const checkPremium = (user) => {
  console.log("PREMIUM CHECK =>", {
    id: user?.id,
    isPremium: user?.isPremium,
    premium: user?.premium,
    is_premium: user?.is_premium,
    type: typeof user?.isPremium,
  });

  return (
    user?.isPremium === true ||
    user?.isPremium === "true" ||
    user?.isPremium === 1 ||
    user?.isPremium === "1" ||
    user?.isPremium === "PREMIUM" ||
    user?.is_premium === true ||
    user?.is_premium === "true" ||
    user?.is_premium === 1 ||
    user?.is_premium === "1" ||
    user?.premium === true ||
    user?.premium === "true" ||
    user?.premium === 1 ||
    user?.premium === "1" ||
    user?.premium === "premium" ||
    user?.premium === "PREMIUM"
  );
};

const getAssignedTo = (user) => {
  const value =
    user?.assigned_to ??
    user?.AssignedTo ??
    user?.assignedTo ??
    user?.assignedto ??
    user?.assigned_person_name ??
    "";

  if (value === null || value === undefined) return "";

  const normalized = String(value).trim();
  return normalized && normalized.toLowerCase() !== "n/a" ? normalized : "";
};

const columns = [
  {
    label: "Completion",
    accessor: "progress",
    cellRenderer: (row) => (
      <div className="flex flex-col gap-1 min-w-[100px]">
        <div className="flex justify-between items-center text-[9px] font-black text-slate-500 uppercase tracking-tighter">
          <span>Progress</span>
          <span
            className={
              row.progress >= 100 ? "text-emerald-400" : "text-blue-400"
            }
          >
            {row.progress || 0}%
          </span>
        </div>
        <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden border border-slate-700/50">
          <div
            className={`h-full transition-all duration-500 rounded-full ${
              row.progress >= 100
                ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]"
                : "bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.4)]"
            }`}
            style={{ width: `${Math.min(100, row.progress || 0)}%` }}
          />
        </div>
      </div>
    ),
  },
  {
    label: "Actions",
    accessor: "service",
    cellRenderer: (row, handleRunService, handleGenerateAboutMe) => (
      <UserActionMenu
        row={row}
        onRun={handleRunService}
        onGenerateAboutMe={handleGenerateAboutMe}
      />
    ),
  },
  {
    label: "User ID",
    accessor: "id",
    cellRenderer: (row) => (
      <span className="font-mono text-[11px] text-slate-500 uppercase tracking-tighter">
        #{row.id}
      </span>
    ),
  },
  {
    label: "Member Identity",
    accessor: "name",
    cellRenderer: (row) => (
      <div className="flex items-center gap-3">
        <UserAvatar name={row.name} photo={row.photo} />
        {console.log("row data for Member Identity =>", row)}
       
        <div>
          <div className="flex items-center gap-1.5 leading-none mb-1">
            <span className="text-sm font-bold text-white">{row.name}</span>
            {checkPremium(row) && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[9px] font-black uppercase tracking-widest">
                ⭐ Premium
              </span>
            )}
          </div>
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
            {row.gender} • {row.age || "??"}y
          </div>
        </div>
      </div>
    ),
  },
  {
    label: "Assigned To",
    accessor: "assigned_to",
    cellRenderer: (row) => (
      row.assigned_to ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-500/15 border border-indigo-500/35 text-indigo-400 text-[9px] font-black uppercase tracking-widest shadow-[0_2px_8px_rgba(99,102,241,0.08)]">
          👤 {row.assigned_to}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-900 border border-slate-800 text-slate-500 text-[9px] font-black uppercase tracking-widest">
          Unassigned
        </span>
      )
    ),
  },
  {
    label: "Overview",
    accessor: "overview",
    cellRenderer: (row, _unused1, _unused2, _unused3, onOverviewClick) => (
      <button
        onClick={(e) => {
          if (onOverviewClick) {
            e.stopPropagation();
            onOverviewClick(row.id, row.name);
          }
        }}
        className="px-2.5 py-1 bg-indigo-500/10 border border-indigo-500/25 hover:bg-indigo-500/20 text-indigo-400 hover:text-white rounded-md text-[10px] font-black uppercase tracking-wider transition-all active:scale-95"
      >
        See Overview
      </button>
    ),
  },
  {
    label: "Connectivity",
    accessor: "email",
    cellRenderer: (row) => (
      <div className="space-y-0.5">
        <div className="text-xs text-slate-300 font-medium">{row.phone}</div>
        <div className="text-[10px] text-slate-500 font-bold">{row.email}</div>
    
      </div>
    ),
  },
  {
    label: "Status",
    accessor: "status",
    cellRenderer: (row) => (
      <span
        className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter border ${
          row.status === "Active"
            ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
            : "bg-red-500/10 text-red-500 border-red-500/20"
        }`}
      >
        {row.status}
      </span>
    ),
  },
  {
    label: "App Status",
    accessor: "app_status",
    cellRenderer: (row) => {
      const rawStatus = (row.app_status || "not_installed").toString().toLowerCase();
      const platform = (row.app_platform || "").toString().toUpperCase();
      const version = row.app_version;

      let badgeClass = "bg-slate-800/60 text-slate-400 border-slate-700/50";
      let statusLabel = "Not Installed";
      let isLivePulse = false;

      if (rawStatus === "online") {
        badgeClass = "bg-emerald-500/15 text-emerald-400 border-emerald-500/30 shadow-[0_0_12px_rgba(16,185,129,0.15)]";
        statusLabel = "Online Now";
        isLivePulse = true;
      } else if (rawStatus === "installed" || rawStatus === "active" || rawStatus === "active_today") {
        badgeClass = "bg-sky-500/15 text-sky-400 border-sky-500/30";
        statusLabel = "Installed";
      } else {
        badgeClass = "bg-slate-800/60 text-slate-400 border-slate-700/50";
        statusLabel = "Not Installed";
      }

      return (
        <div className="flex flex-col gap-1 min-w-[110px]">
          <div className="inline-flex items-center">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${badgeClass}`}
            >
              {isLivePulse && (
                <span className="relative flex h-2 w-2 mr-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
              )}
              {statusLabel}
            </span>
          </div>
          {statusLabel === "Not Installed" ? (
            <span className="text-[10px] text-slate-500 font-medium italic">
              {platform ? "Not active > 2mo" : "Never logged in"}
            </span>
          ) : (platform || version) ? (
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 tracking-tight">
              {platform === "ANDROID" ? (
                <span className="text-[11px] leading-none" title="Android">🤖</span>
              ) : platform === "IOS" ? (
                <span className="text-[11px] leading-none" title="iOS">🍎</span>
              ) : null}
              <span>{platform || "Mobile"}</span>
              {version && (
                <span className="text-slate-400 font-mono text-[9px] bg-slate-800/90 px-1 py-0.5 rounded border border-slate-700/60">
                  v{version}
                </span>
              )}
            </div>
          ) : (
            <span className="text-[10px] text-slate-500 font-medium italic">
              Mobile app
            </span>
          )}
        </div>
      );
    },
  },
  {
    label: "Caste",
    accessor: "caste",
    cellRenderer: (row) => (
      <span className="text-xs text-slate-300 font-medium">
        {row.caste || "N/A"}
      </span>
    ),
  },
  {
    label: "Verified",
    accessor: "verified",
    cellRenderer: (row, _, handleVerifyClick) => {
      const isComplete = (row.progress || 0) >= 100;
      return (
        <div className="flex items-center justify-center">
          {row.verified ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-blue-500"
              viewBox="0 0 20 20"
              fill="currentColor"
              title="Verified"
            >
              <path
                fillRule="evenodd"
                d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isComplete) handleVerifyClick(row);
              }}
              disabled={!isComplete}
              className={`transition-all ${isComplete ? "hover:scale-110 cursor-pointer text-slate-600 hover:text-yellow-500" : "cursor-not-allowed opacity-50 text-slate-700"}`}
              title={
                isComplete
                  ? "Click to verify"
                  : "Profile must be 100% complete to verify"
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
            </button>
          )}
        </div>
      );
    },
  },
  {
    label: "Version",
    accessor: "version",
    cellRenderer: (row) => (
      <span className="text-[10px] font-bold text-slate-500 bg-slate-800/50 px-2 py-0.5 rounded border border-slate-700/50 uppercase">
        v{row.version || "1.0"}
      </span>
    ),
  },
  {
    label: "Matches",
    accessor: "matchCount",
    cellRenderer: (row, _unused1, _unused2, onCountClick) => (
      <button
        onClick={(e) => {
          if (onCountClick) {
            e.stopPropagation();
            onCountClick(row.id, "matches", row.name);
          }
        }}
        className="px-2.5 py-1 bg-blue-500/10 border border-blue-500/25 hover:bg-blue-500/20 text-blue-400 hover:text-white rounded-md text-[10px] font-black uppercase tracking-wider transition-all"
      >
        View
      </button>
    ),
  },
  {
    label: "Queue",
    accessor: "queueCount",
    cellRenderer: (row, _unused1, _unused2, onCountClick) => (
      <button
        onClick={(e) => {
          if (onCountClick) {
            e.stopPropagation();
            onCountClick(row.id, "queue", row.name);
          }
        }}
        className="px-2.5 py-1 bg-purple-500/10 border border-purple-500/25 hover:bg-purple-500/20 text-purple-400 hover:text-white rounded-md text-[10px] font-black uppercase tracking-wider transition-all"
      >
        View
      </button>
    ),
  },
  {
    label: "Publish",
    accessor: "publishCount",
    cellRenderer: (row, _unused1, _unused2, onCountClick) => (
      <button
        onClick={(e) => {
          if (onCountClick) {
            e.stopPropagation();
            onCountClick(row.id, "publish", row.name);
          }
        }}
        className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/25 hover:bg-emerald-500/20 text-emerald-400 hover:text-white rounded-md text-[10px] font-black uppercase tracking-wider transition-all"
      >
        View
      </button>
    ),
  },
  {
    label: "Likes Sent",
    accessor: "likesSentCount",
    cellRenderer: (row, _unused1, _unused2, onCountClick) => (
      <button
        onClick={(e) => {
          if (onCountClick) {
            e.stopPropagation();
            onCountClick(row.id, "likes_sent", row.name);
          }
        }}
        className="px-2.5 py-1 bg-blue-500/10 border border-blue-500/25 hover:bg-blue-500/20 text-blue-400 hover:text-white rounded-md text-[10px] font-black uppercase tracking-wider transition-all"
      >
        View
      </button>
    ),
  },
  {
    label: "Dislikes Sent",
    accessor: "dislikesSentCount",
    cellRenderer: (row, _unused1, _unused2, onCountClick) => (
      <button
        onClick={(e) => {
          if (onCountClick) {
            e.stopPropagation();
            onCountClick(row.id, "dislikes_sent", row.name);
          }
        }}
        className="px-2.5 py-1 bg-rose-500/10 border border-rose-500/25 hover:bg-rose-500/20 text-rose-400 hover:text-white rounded-md text-[10px] font-black uppercase tracking-wider transition-all"
      >
        View
      </button>
    ),
  },
  {
    label: "Liked By",
    accessor: "likedByCount",
    cellRenderer: (row, _unused1, _unused2, onCountClick) => (
      <button
        onClick={(e) => {
          if (onCountClick) {
            e.stopPropagation();
            onCountClick(row.id, "likes_received", row.name);
          }
        }}
        className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/25 hover:bg-emerald-500/20 text-emerald-400 hover:text-white rounded-md text-[10px] font-black uppercase tracking-wider transition-all"
      >
        View
      </button>
    ),
  },
  {
    label: "Skipped",
    accessor: "skippedCount",
    cellRenderer: (row) => (
      <span
        className="text-rose-500 text-[10px] font-black uppercase tracking-tighter"
        title="Skipped Count"
      >
        {row.skippedCount || 0}
      </span>
    ),
  },
  {
    label: "Potential Match",
    accessor: "potentialCount",
    cellRenderer: (row) => (
      <span
        className="text-amber-500 text-[10px] font-black uppercase tracking-tighter"
        title="Potential Match"
      >
        {row.potentialCount || 0}
      </span>
    ),
  },
  {
    label: "Location",
    accessor: "location",
    cellRenderer: (row) => (
      <span className="text-xs text-slate-400 font-medium italic">
        {row.location || "N/A"}
      </span>
    ),
  },
  {
    label: "Created",
    accessor: "registered",
    cellRenderer: (row) => {
      if (!row.registered)
        return <span className="text-xs text-slate-600">N/A</span>;
      const createdDate = new Date(row.registered);
      const now = new Date();
      const diffTime = Math.abs(now - createdDate);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return (
        <div className="space-y-0.5">
          <div className="text-xs text-slate-300 font-medium">
            {createdDate.toLocaleDateString()}
          </div>
          <div className="text-[10px] text-slate-500 font-bold">
            {diffDays}d ago
          </div>
        </div>
      );
    },
  },
];

const MatchStatsModal = ({ show, onClose, type, userName, data, loading }) => {
  if (!show) return null;

  const getFilteredData = () => {
    if (!data) return [];
    if (type === "matches") return data.matches || [];
    if (type === "queue") return data.queue || [];
    if (type === "publish") return (data.matches || []).filter(m => m.published === true);
    if (type === "likes_sent") return data.likes_sent || [];
    if (type === "dislikes_sent") return data.dislikes_sent || [];
    if (type === "likes_received") return data.likes_received || [];
    return [];
  };

  const list = getFilteredData();

  const categoryConfig = {
    matches: {
      label: "All Matches",
      colorClass: "text-blue-400 bg-blue-500/10 border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.1)]",
      glowColor: "rgba(59,130,246,0.15)",
      dateLabel: "Matched On"
    },
    queue: {
      label: "Queue Matches",
      colorClass: "text-purple-400 bg-purple-500/10 border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.1)]",
      glowColor: "rgba(168,85,247,0.15)",
      dateLabel: "Added On"
    },
    publish: {
      label: "Published Matches",
      colorClass: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.1)]",
      glowColor: "rgba(16,185,129,0.15)",
      dateLabel: "Published On"
    },
    likes_sent: {
      label: "Likes Sent",
      colorClass: "text-sky-400 bg-sky-500/10 border-sky-500/20 shadow-[0_0_15px_rgba(14,165,233,0.1)]",
      glowColor: "rgba(14,165,233,0.15)",
      dateLabel: "Liked On"
    },
    dislikes_sent: {
      label: "Dislikes Sent",
      colorClass: "text-rose-400 bg-rose-500/10 border-rose-500/20 shadow-[0_0_15px_rgba(244,63,94,0.1)]",
      glowColor: "rgba(244,63,94,0.15)",
      dateLabel: "Disliked On"
    },
    likes_received: {
      label: "Liked By",
      colorClass: "text-pink-400 bg-pink-500/10 border-pink-500/20 shadow-[0_0_15px_rgba(236,72,153,0.1)]",
      glowColor: "rgba(236,72,153,0.15)",
      dateLabel: "Received On"
    }
  };

  const config = categoryConfig[type] || {
    label: "Dashboard Records",
    colorClass: "text-slate-400 bg-slate-500/10 border-slate-500/20",
    glowColor: "rgba(100,116,139,0.15)",
    dateLabel: "Updated On"
  };

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-slate-950 border border-slate-800 rounded-3xl p-6 max-w-4xl w-full max-h-[85vh] flex flex-col shadow-[0_24px_100px_rgba(0,0,0,0.8)] transition-all duration-300"
        style={{
          boxShadow: `0 24px 100px rgba(0, 0, 0, 0.8), 0 0 40px ${config.glowColor}`,
          borderColor: type ? `${config.glowColor.replace('0.15', '0.3')}` : 'rgb(30, 41, 59)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-4">
          <div>
            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${config.colorClass}`}>
              {config.label}
            </span>
            <h3 className="text-xl font-black text-white uppercase tracking-tight mt-1.5">
              {userName}'s Dashboard Records
            </h3>
          </div>
          <button 
            onClick={onClose}
            className="p-2 bg-slate-900 border border-slate-800 text-slate-400 hover:text-white rounded-xl transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 pr-1 min-h-[300px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-slate-400 text-xs font-black uppercase tracking-widest animate-pulse">Fetching match stats...</p>
            </div>
          ) : list.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <span className="text-4xl mb-3">📂</span>
              <p className="text-white font-bold text-sm">No records found</p>
              <p className="text-slate-500 text-xs mt-1">There are no matches currently listed in this category.</p>
            </div>
          ) : (
            list.map((item, idx) => {
              const score = item.match_score ?? item.llm_score ?? null;
              const dateVal = item.matched_at ?? item.created_at ?? null;
              const formattedDate = dateVal ? new Date(dateVal).toLocaleDateString() : "N/A";
              const photo = item.profile_pic?.compressed || item.photo;
              const candidateId = item.candidate_id || item.user_id || item.uid || item.matrimonial_id || item.id;

              // Score-based colors
              const isHigh = score !== null && score >= 75;
              const isMid = score !== null && score >= 50;
              const scoreBorder = score !== null 
                ? (isHigh ? "border-l-emerald-500" : isMid ? "border-l-indigo-500" : "border-l-amber-500") 
                : "border-l-slate-800";
              const scoreBadgeClass = score !== null
                ? (isHigh ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.1)]" : isMid ? "bg-indigo-500/10 text-indigo-400 border-indigo-500/20 shadow-[0_0_15px_rgba(99,102,241,0.1)]" : "bg-amber-500/10 text-amber-400 border-amber-500/20")
                : "bg-slate-800/80 text-slate-500 border-slate-700/50";

              return (
                <div 
                  key={candidateId || idx}
                  className={`flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-5 rounded-2xl border border-slate-800 bg-slate-900/20 border-l-4 ${scoreBorder} hover:bg-slate-900/50 hover:border-slate-700/80 hover:shadow-[0_12px_40px_rgba(99,102,241,0.06)] hover:-translate-y-0.5 transition-all duration-300 animate-fade-in`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-slate-950 to-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 overflow-hidden relative shrink-0 shadow-inner group-hover:border-indigo-500/40 transition-colors">
                      {photo ? (
                        <img src={photo} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-base font-black text-indigo-400 bg-indigo-500/5 w-full h-full flex items-center justify-center uppercase">
                          {item.name ? item.name[0] : "C"}
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-bold text-white leading-none">
                          {item.name || "Anonymous"}
                        </h4>
                        {candidateId && (
                          <span className="text-[9px] font-mono text-slate-500 uppercase bg-slate-950 px-1.5 py-0.5 rounded border border-slate-800">
                            #{candidateId}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 font-semibold mt-1.5 uppercase tracking-wider">
                        {item.gender} • {item.age} Years • {item.height || "N/A"}
                      </div>
                      <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">
                        📍 {item.location || `${item.city || ""}, ${item.state || ""}`.trim().replace(/^,|,$/, "") || "N/A"}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:flex md:items-center gap-x-8 gap-y-2 w-full md:w-auto text-left md:text-right border-t border-slate-800/40 pt-3 md:pt-0 md:border-none">
                    <div>
                      <span className="block text-[8px] font-black text-slate-500 uppercase tracking-widest">Profession</span>
                      <span className="text-xs font-semibold text-slate-300 block mt-0.5">{item.profession || "N/A"}</span>
                    </div>
                    <div>
                      <span className="block text-[8px] font-black text-slate-500 uppercase tracking-widest">Income</span>
                      <span className="text-xs font-semibold text-slate-300 block mt-0.5">{item.income || "N/A"}</span>
                    </div>
                    <div>
                      <span className="block text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1">Score</span>
                      <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-black tracking-tight border uppercase ${scoreBadgeClass}`}>
                        {score !== null ? `${score}%` : "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="block text-[8px] font-black text-slate-500 uppercase tracking-widest">{config.dateLabel}</span>
                      <span className="text-xs font-semibold text-slate-400 block mt-0.5">{formattedDate}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 w-full md:w-auto mt-2 md:mt-0 shrink-0 border-t border-slate-800/40 pt-3 md:pt-0 md:border-none">
                    {candidateId ? (
                      <>
                        <a
                          href={`/users/user/${candidateId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 md:flex-initial px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl shadow-lg shadow-indigo-600/15 hover:shadow-indigo-600/25 transition-all text-[10px] font-black uppercase tracking-wider text-center active:scale-95"
                        >
                          Profile
                        </a>
                        <a
                          href={`/matches?user_id=${encodeURIComponent(candidateId)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 md:flex-initial px-4 py-2 bg-slate-950 border border-slate-800 text-slate-300 rounded-xl hover:bg-slate-800 hover:text-white hover:border-slate-700 transition-all text-[10px] font-black uppercase tracking-wider text-center active:scale-95"
                        >
                          Matches
                        </a>
                      </>
                    ) : (
                      <span className="text-[10px] text-slate-600 uppercase font-black tracking-wider px-4 py-2">
                        No Actions
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const MatchStatsOverviewModal = ({ show, onClose, userName, data, loading }) => {
  if (!show) return null;

  const totals = data?.totals || {};
  
  const chartData = [
    { name: "Published Matches", value: totals.published_matches || 0, color: "#10b981" },
    { name: "Unpublished Matches", value: totals.unpublished_matches || 0, color: "#f59e0b" },
    { name: "Queue Matches", value: totals.queue_matches || 0, color: "#a855f7" },
    { name: "Likes Sent", value: totals.likes_sent || 0, color: "#3b82f6" },
    { name: "Dislikes Sent", value: totals.dislikes_sent || 0, color: "#ef4444" },
    { name: "Liked By", value: totals.likes_received || 0, color: "#06b6d4" },
  ].filter(item => item.value > 0);

  const hasData = chartData.length > 0;

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-slate-950 border border-slate-800 rounded-3xl p-6 max-w-3xl w-full max-h-[85vh] flex flex-col shadow-[0_24px_100px_rgba(0,0,0,0.8)] transition-all duration-300"
        style={{
          boxShadow: `0 24px 100px rgba(0, 0, 0, 0.8), 0 0 40px rgba(99, 102, 241, 0.15)`,
          borderColor: 'rgba(99, 102, 241, 0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-4">
          <div>
            <span className="inline-flex px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border text-indigo-400 bg-indigo-500/10 border-indigo-500/20 shadow-[0_0_15px_rgba(99,102,241,0.1)]">
              Activity Overview
            </span>
            <h3 className="text-xl font-black text-white uppercase tracking-tight mt-1.5">
              {userName}'s Analytics
            </h3>
          </div>
          <button 
            onClick={onClose}
            className="p-2 bg-slate-900 border border-slate-800 text-slate-400 hover:text-white rounded-xl transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 min-h-[300px] flex flex-col justify-center">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <svg className="animate-spin h-8 w-8 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-slate-400 text-xs font-black uppercase tracking-widest animate-pulse">Loading overview charts...</p>
            </div>
          ) : !hasData ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <span className="text-4xl mb-3">📊</span>
              <p className="text-white font-bold text-sm">No activity recorded</p>
              <p className="text-slate-500 text-xs mt-1">There is no matchmaking activity or statistics history for this user yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              {/* Pie Chart Section */}
              <div className="h-[280px] w-full flex items-center justify-center relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "#020617",
                        border: "1px solid rgba(51, 65, 85, 0.9)",
                        borderRadius: 16,
                      }}
                      itemStyle={{
                        color: "#e2e8f0",
                        fontSize: "11px",
                        fontWeight: "bold",
                        fontFamily: "monospace"
                      }}
                      labelStyle={{
                        color: "#e2e8f0",
                        fontSize: "11px",
                        fontWeight: "bold",
                        fontFamily: "monospace"
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Legends & Detailed Table Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-slate-800 pb-2 mb-2">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-wider">
                    Match Activity Statistics
                  </h4>
                  <span className="inline-flex px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border border-indigo-500/20 bg-indigo-500/10 text-indigo-400">
                    Total: {totals.total_matches || 0}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "Published Matches", val: totals.published_matches || 0, colorClass: "text-emerald-400 border-emerald-500/20 bg-emerald-500/5" },
                    { label: "Unpublished Matches", val: totals.unpublished_matches || 0, colorClass: "text-amber-400 border-amber-500/20 bg-amber-500/5" },
                    { label: "Queue Matches", val: totals.queue_matches || 0, colorClass: "text-purple-400 border-purple-500/20 bg-purple-500/5" },
                    { label: "Likes Sent", val: totals.likes_sent || 0, colorClass: "text-blue-400 border-blue-500/20 bg-blue-500/5" },
                    { label: "Dislikes Sent", val: totals.dislikes_sent || 0, colorClass: "text-red-400 border-red-500/20 bg-red-500/5" },
                    { label: "Liked By", val: totals.likes_received || 0, colorClass: "text-teal-400 border-teal-500/20 bg-teal-500/5" },
                  ].map((item) => (
                    <div key={item.label} className={`flex flex-col p-2.5 rounded-xl border ${item.colorClass}`}>
                      <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider">{item.label}</span>
                      <span className="text-base font-black mt-1">{item.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const Users = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const profileStatus = searchParams.get('status');
  const premiumStatus = searchParams.get('premium');
  const communityParam = searchParams.get('community');
  const savedState = useMemo(() => {
    try {
      const data = sessionStorage.getItem('users_full_state');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const [view, setView] = useState("table");
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams.get('tab') || savedState?.activeTab || "registered";
  });
  const [query, setQuery] = useState(() => {
    return searchParams.get('q') !== null ? (searchParams.get('q') || "") : (savedState?.query || "");
  });

  const [page, setPage] = useState(() => {
    return Number(searchParams.get('page')) || savedState?.page || 1;
  });

  const [sortConfig, setSortConfig] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [verifyModal, setVerifyModal] = useState(null);
  const [errorModal, setErrorModal] = useState({ show: false, message: "", variant: "error" });
  const [dropdownOptions, setDropdownOptions] = useState({
    salaryOptions: [],
    qualificationOptions: [],
    professionOptions: [],
    cityOptions: [],
    stateOptions: [],
    communityOptions: [],
    genderOptions: [],
    maritalStatusOptions: [],
    manglikOptions: [],
    familyTypeOptions: [],
    familyBackgroundOptions: [],
    assignedToOptions: [],
    heightOptions: [],
  });
  const [isFiltersMinimized, setIsFiltersMinimized] = useState(() => {
    return localStorage.getItem("users_filters_minimized") === "true";
  });
  const [filters, setFilters] = useState(() => {
    const saved = savedState?.filters || {};
    return {
      salary: searchParams.get('salary') || saved.salary || '',
      age: searchParams.get('age') || saved.age || '',
      qualification: searchParams.get('qualification') || saved.qualification || '',
      profession: searchParams.get('profession') || saved.profession || '',
      premium: premiumStatus || searchParams.get('premium') || saved.premium || '',
      gender: searchParams.get('gender') || saved.gender || '',
      city: searchParams.get('city') || saved.city || '',
      state: searchParams.get('state') || saved.state || '',
      community: communityParam || searchParams.get('community') || saved.community || '',
      manglik: searchParams.get('manglik') || saved.manglik || '',
      marital_status: searchParams.get('marital_status') || saved.marital_status || '',
      family_type: searchParams.get('family_type') || saved.family_type || '',
      family_background: searchParams.get('family_background') || saved.family_background || '',
      assigned_to: searchParams.get('assigned_to') || saved.assigned_to || '',
      min_height: searchParams.get('min_height') || saved.min_height || '',
      max_height: searchParams.get('max_height') || saved.max_height || '',
    };
  });

  const hasActiveFilters = Boolean(
    filters.salary || filters.age || filters.qualification || filters.profession ||
    filters.premium || filters.gender || filters.city || filters.state ||
    filters.community || filters.manglik || filters.marital_status ||
    filters.family_type || filters.family_background || filters.assigned_to ||
    filters.min_height || filters.max_height
  );

  const activeFiltersCount = useMemo(() => {
    return [
      filters.salary, filters.age, filters.qualification, filters.profession,
      filters.premium, filters.gender, filters.city, filters.state,
      filters.community, filters.manglik, filters.marital_status,
      filters.family_type, filters.family_background, filters.assigned_to,
      filters.min_height, filters.max_height
    ].filter(Boolean).length;
  }, [filters]);

  const [rowsPerPage, setRowsPerPage] = useState(() => {
    return Number(searchParams.get('limit')) || savedState?.rowsPerPage || 10;
  });

  const handleClearAllFilters = useCallback(() => {
    const emptyFilters = {
      salary: '', age: '', qualification: '', profession: '',
      premium: '', gender: '', city: '', state: '',
      community: '', manglik: '', marital_status: '',
      family_type: '', family_background: '', assigned_to: '',
      min_height: '', max_height: ''
    };
    setFilters(emptyFilters);
    setPage(1);
    sessionStorage.removeItem('users_full_state');
    sessionStorage.removeItem('users_last_search');
    const newParams = new URLSearchParams();
    if (activeTab && activeTab !== "registered") {
      newParams.set('tab', activeTab);
    }
    navigate({ pathname: '/users', search: newParams.toString() });
  }, [activeTab, navigate]);

  // Sync page, rowsPerPage, query, activeTab, and all filters with URL search params & sessionStorage
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    let changed = false;

    const currentPageInUrl = Number(newParams.get('page')) || 1;
    if (page !== currentPageInUrl) {
      if (page > 1) {
        newParams.set('page', page.toString());
      } else {
        newParams.delete('page');
      }
      changed = true;
    }

    const currentLimitInUrl = Number(newParams.get('limit')) || 10;
    if (rowsPerPage !== currentLimitInUrl) {
      if (rowsPerPage !== 10) {
        newParams.set('limit', rowsPerPage.toString());
      } else {
        newParams.delete('limit');
      }
      changed = true;
    }

    const currentQueryInUrl = newParams.get('q') || "";
    if (query !== currentQueryInUrl) {
      if (query) {
        newParams.set('q', query);
      } else {
        newParams.delete('q');
      }
      changed = true;
    }

    const currentTabInUrl = newParams.get('tab') || "registered";
    if (activeTab !== currentTabInUrl) {
      if (activeTab && activeTab !== "registered") {
        newParams.set('tab', activeTab);
      } else {
        newParams.delete('tab');
      }
      changed = true;
    }

    const filterKeys = [
      'salary', 'age', 'qualification', 'profession', 'gender',
      'city', 'state', 'community', 'manglik', 'marital_status',
      'family_type', 'family_background', 'assigned_to', 'min_height', 'max_height'
    ];

    filterKeys.forEach((key) => {
      const val = filters[key] || "";
      const urlVal = newParams.get(key) || "";
      if (val !== urlVal) {
        if (val) {
          newParams.set(key, val);
        } else {
          newParams.delete(key);
        }
        changed = true;
      }
    });

    if (changed) {
      navigate({ search: newParams.toString() }, { replace: true });
    }

    // Persist full state for sidebar navigation & returning to page
    sessionStorage.setItem('users_last_search', newParams.toString());
    sessionStorage.setItem('users_full_state', JSON.stringify({
      filters,
      page,
      activeTab,
      rowsPerPage,
      query,
    }));
  }, [page, rowsPerPage, query, activeTab, filters, navigate, searchParams]);


  const [editingPhoneUserId, setEditingPhoneUserId] = useState(null);
  const [editedPhoneValue, setEditedPhoneValue] = useState("");
  const [editingCasteUserId, setEditingCasteUserId] = useState(null);
  const [editedCasteValue, setEditedCasteValue] = useState("");

  const isSuccessResponse = useCallback(
    (response) => response?.status === "success" || response?.status === true || response?.status === 200,
    []
  );

  const buildErrorMessage = (response) => {
    const message = response?.message || "Operation failed";

    if (Array.isArray(response?.missing_fields) && response.missing_fields.length > 0) {
      return `${message}\n\nMissing fields: ${response.missing_fields.join(", ")}`;
    }

    return message;
  };

  const handleUpdatePhone = async (userId) => {
    const val = editedPhoneValue.trim();
    if (!val) {
      toast.error("Phone number cannot be empty");
      return;
    }
    if (val.length !== 10) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }
    try {
      LoaderService.show();
      const response = await PatchUpdatePhoneNumber(userId, editedPhoneValue.trim());
      console.log("Update Phone API Response:", response);
      
      const isSuccess = response.status === "success" || response.status === true || response.status === 200;
      
      if (isSuccess) {
        setEditingPhoneUserId(null);
        setEditedPhoneValue("");
        
        console.log("Status is success, reloading page in 1.5 seconds...");
        setTimeout(() => {
          window.location.reload();
        }, 1500); 
      }
    } catch (error) {
      console.error("Error updating phone number:", error);
      toast.error("An error occurred while updating phone number");
    } finally {
      LoaderService.hide();
    }
  };

  const handleUpdateCaste = async (userId) => {
    const val = editedCasteValue.trim();
    if (!val) {
      toast.error("Caste/Community cannot be empty");
      return;
    }
    try {
      LoaderService.show();
      const response = await PatchUpdateUserCommunityCaste(userId, val);
      if (isSuccessResponse(response)) {
        setEditingCasteUserId(null);
        setEditedCasteValue("");
        fetchUsers(page);
      }
    } catch (error) {
      console.error("Error updating caste:", error);
      toast.error(error?.message || "An error occurred while updating caste");
    } finally {
      LoaderService.hide();
    }
  };

  const isFilterMount = useRef(true);
  useEffect(() => {
    if (isFilterMount.current) {
      isFilterMount.current = false;
      return;
    }
    setPage(1);
  }, [profileStatus, premiumStatus, communityParam]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      premium: premiumStatus || '',
      community: communityParam || '',
    }));
  }, [premiumStatus, communityParam]);

  // Fetch dropdown options for filters
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [dropdownResponse, stateCityResponse, communityStatsResponse, teamResponse] = await Promise.all([
          GetDropdownData(),
          GetStateCityDropdown(),
          GetCommunityStatistics(),
          GetCustomerSupportAdmins(),
        ]);

        const dropdownData = dropdownResponse?.data || {};
        const stateCityData = stateCityResponse?.data || {};

        const sortValues = (values) => Array.isArray(values) ? values.slice().sort((a, b) => String(a).localeCompare(String(b))) : [];
        const getOptions = (...keys) => {
          for (const key of keys) {
            const value = dropdownData[key];
            if (Array.isArray(value)) return sortValues(value);
          }
          return [];
        };

        const communityOptions = Array.isArray(communityStatsResponse?.data)
          ? communityStatsResponse.data
              .map((item) => item?.community)
              .filter((value) => typeof value === "string" && value.trim() !== "")
          : [];

        setDropdownOptions({
          salaryOptions: dropdownData.incomeoptions || [],
          qualificationOptions: getOptions('qualification', 'Qualification', 'qualifications', 'Qualifications', 'qualificationoptions', 'QualificationOptions', 'qualificationOptions'),
          professionOptions: sortValues(dropdownData.Profession || dropdownData.profession || dropdownData.Professions || dropdownData.professions || []),
          cityOptions: sortValues(stateCityData.cities || []),
          stateOptions: sortValues(stateCityData.states || []),
          communityOptions: sortValues([...new Set(communityOptions)]),
          manglikOptions: getOptions('manglik', 'Manglik', 'mangliks', 'Mangliks', 'manglikoptions', 'ManglikOptions', 'manglikOptions'),
          genderOptions: getOptions('gender', 'Gender', 'genderoptions', 'GenderOptions', 'genderOptions').length > 0
            ? getOptions('gender', 'Gender', 'genderoptions', 'GenderOptions', 'genderOptions')
            : ["Male", "Female", "Other"],
          maritalStatusOptions: getOptions('profile_marital_option', 'marital_status', 'maritalStatus', 'MaritalStatus', 'maritalStatuses', 'MaritalStatuses', 'maritalstatusoptions', 'MaritalStatusOptions', 'maritalStatusOptions'),
          familyTypeOptions: getOptions('family_type', 'FamilyType', 'familyType', 'familyTypes', 'FamilyTypes', 'familytypeoptions', 'FamilyTypeOptions', 'familyTypeOptions'),
          familyBackgroundOptions: getOptions('family_background', 'FamilyBackground', 'familyBackground', 'familyBackgrounds', 'FamilyBackgrounds', 'familybackgroundoptions', 'FamilyBackgroundOptions', 'familyBackgroundOptions'),
          heightOptions: getOptions('heightoptions', 'heightOptions', 'HeightOptions', 'height'),
          assignedToOptions: sortValues(
            (teamResponse?.data || [])
              .filter((admin) => admin?.is_active !== false)
              .map((admin) => admin?.username)
              .filter(Boolean),
          ),
        });
      } catch (error) {
        console.error("Failed to fetch dropdown data", error);
      }
    };
    fetchDropdownData();
  }, []);

  /*
  const handleExport = async () => {
    try {
      LoaderService.show();
      await GetExportUsers();
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      LoaderService.hide();
    }
  };
  */

  const handleRunService = useCallback(async (userId) => {
    try {
      LoaderService.show();
      const payload = {
        service: "common_service",
        uid: userId,
        cid: ""
      };
      const response = await PostAdminService(payload);
      if (response.status === "success" || response.status === true) {
        // Success toast already handled by ApiRequest
      }
    } catch (error) {
      console.error("Service execution failed:", error);
      toast.error("An error occurred while running the service");
    } finally {
      LoaderService.hide();
    }
  }, []);

  const handleGenerateAboutMe = useCallback(async (user) => {
    const toastId = `about-me-${user.id}`;
    let progress = 0;
    const startTime = Date.now();

    toast.custom(
      <AboutMeProgressToast progress={progress} userName={user.name} />,
      { id: toastId, duration: Infinity }
    );

    const intervalId = window.setInterval(() => {
      const elapsed = Date.now() - startTime;

      if (elapsed <= 3000) {
        progress = (elapsed / 3000) * 50;
      } else if (elapsed <= 5000) {
        progress = 50 + ((elapsed - 3000) / 2000) * 11;
      } else if (elapsed <= 22000) {
        progress = 61 + ((elapsed - 5000) / 17000) * 29;
      } else {
        progress = 90;
      }

      toast.custom(
        <AboutMeProgressToast progress={progress} userName={user.name} />,
        { id: toastId, duration: Infinity }
      );

      if (progress >= 90) {
        window.clearInterval(intervalId);
      }
    }, 100);

    try {
      const response = await PostGenerateAboutMe({ user_id: user.id });

      if (isSuccessResponse(response)) {
        window.clearInterval(intervalId);
        toast.custom(
          <AboutMeProgressToast
            progress={100}
            userName={user.name}
            status="success"
          />,
          { id: toastId, duration: 1600 }
        );
        toast.success(response.message || "About Me generated successfully");
      } else {
        window.clearInterval(intervalId);
        toast.dismiss(toastId);
        toast.error(response?.message || "Failed to generate About Me");
      }
    } catch (error) {
      window.clearInterval(intervalId);
      toast.dismiss(toastId);
      console.error("About Me generation failed:", error);
      toast.error("An error occurred while generating About Me");
    }
  }, [isSuccessResponse]);

  const handleVerifyUser = useCallback(async (userId) => {
    try {
      LoaderService.show();
      const response = await PostTriggerServices(userId, true);
      // Check for success - matches the logic in AdminServices.tsx
      if (response.status === "success" || response.status === true) {
        setUsersData(prev => prev.map(u => u.id === userId ? { ...u, verified: true } : u));
      } else {
        setErrorModal({
          show: true,
          message: buildErrorMessage(response),
          variant: "error"
        });
      }
    } catch (error) {
      console.error("Verification failed:", error);
      setErrorModal({
        show: true,
        message: "An error occurred while verifying user",
        variant: "error"
      });
    } finally {
      LoaderService.hide();
      setVerifyModal(null);
    }
  }, []);



  const searchUsers = useCallback(async (searchTerm) => {
    setIsSearching(true);
    LoaderService.show();
    try {
      const offset = (page - 1) * rowsPerPage;
      let rawUsers = [];
      let total = 0;

      if (activeTab === "premium") {
        const response = await getMembersOverview({
          search: searchTerm,
          premium_filter: "premium",
          limit: rowsPerPage,
          offset,
        });
        const normalized = response?.data || {};
        rawUsers = normalized.rows || [];
        total = normalized.pagination?.total || rawUsers.length || 0;
      } else {
        const response = await SearchUsers(searchTerm, rowsPerPage, offset, filters.assigned_to);
        if (response.status === "success") {
          rawUsers = response.data || [];
          total = response.count || 0;
        }
      }

      setTotalCount(total);
      console.log(`[Search] Found ${rawUsers.length} users for: "${searchTerm}"`);

      const mappedUsers = rawUsers.map((u) => ({
        ...u,
        id: u.user_id || u.UserId || u.id,
        name:
          u.name ||
          (u.FirstName || u.LastName
            ? `${u.FirstName || ""} ${u.LastName || ""}`.trim()
            : ""),
        gender: u.gender || u.Gender,
        stage: u.stage || u.Stage,
        progress: u.progress || u.Progress || 0,
        email: u.email || u.Email || "",
        phone: u.phone || u.Phone || u.phone_number || "",
        matchCount: u.matchCount || u.MatchCount || u.total_matches_published || 0,
        queueCount: u.queueCount || u.QueueCount || u.total_matches_unpublished || 0,
        publishCount:
          u.publishCount || u.PublishCount || u.publish_count || u.total_matches_published || 0,
        status: u.status || u.Status || "Active",
        age: u.age || u.Age || "",
        caste: u.caste || u.Caste || "",
        address: u.address || u.Address || u.City || "",
        location:
          u.location || u.Location || (u.city || u.state ? `${u.city || ""}, ${u.state || ""}`.trim().replace(/^,|,$/, "") : "N/A"),
        registered: u.created_at || u.CreatedAt || u.registered || "",
        manglik: u.manglik || u.Manglik || "",
        assigned_to: getAssignedTo(u),
        tagAllocated: u.tagAllocated || u.TagAllocated || 0,
        photo: u.photo || u.Photo || "",
        version: u.version || u.Version || "1.0",
        totalCredits: u.credits || u.TotalCredits || u.totalCredits || 0,
        creditsUsed: u.creditsUsed || u.CreditsUsed || 0,
        skippedCount:
          u.skippedCount || u.SkippedCount || u.skipped_count || 0,
        potentialCount:
          u.potentialCount || u.PotentialCount || u.potential_count || 0,
        verified: u.verified || u.is_verified || false,
        isPremium: activeTab === "premium" ? true : checkPremium(u),
        premium: u?.premium,
        is_premium: u?.is_premium,
        app_status: u.app_status || "not_installed",
        app_platform: u.app_platform || null,
        app_version: u.app_version || null,
        app_last_seen_at: u.app_last_seen_at || null,
      }));

      setUsersData(mappedUsers);
    } catch (error) {
      console.error("[Search] Error searching users:", error);
      setUsersData([]);
    } finally {
      LoaderService.hide();
      setIsSearching(false);
    }
  }, [page, rowsPerPage, activeTab, filters.assigned_to]);

  const fetchUsers = useCallback(async (currentPage = 1) => {
    if (query && query.trim().length >= 2) return; // Skip if searching

    LoaderService.show();
    try {
      const offset = (currentPage - 1) * rowsPerPage;
      let rawUsers = [];
      let total = 0;

      if (activeTab === "premium") {
        const reqFilters = {
          premium_filter: "premium",
          limit: rowsPerPage,
          offset: offset,
          gender: filters.gender,
          city: filters.city,
          state: filters.state,
        };
        const response = await getMembersOverview(reqFilters);
        const normalized = response?.data || {};
        rawUsers = normalized.rows || [];
        total = normalized.pagination?.total || rawUsers.length || 0;
      } else {
        const response = await GetUsers(rowsPerPage, offset, profileStatus, filters, activeTab);
        rawUsers = response.data || response.users || [];
        total = response.count || 0;
      }

      setTotalCount(total);

      const mappedUsers = rawUsers.map(u => ({
        ...u,
        id: u.UserId || u.id || u.user_id,
        name: (u.FirstName || u.LastName) ? `${u.FirstName || ''} ${u.LastName || ''}`.trim() : u.name || "",
        gender: u.Gender || u.gender,
        stage: u.Stage || u.stage,
        progress: u.progress || u.Progress || 0,
        email: u.Email || u.email || "",
        phone: u.Phone || u.phone || u.phone_number || "",
        matchCount: u.MatchCount || u.matchCount || u.total_matches_published || 0,
        queueCount: u.QueueCount || u.queueCount || u.total_matches_unpublished || 0,
        publishCount: u.PublishCount || u.publishCount || u.publish_count || u.total_matches_published || 0,
        status: u.Status || u.status || "Active",
        age: u.Age || u.age || "",
        caste: u.Caste || u.caste || "",
        address: u.Address || u.City || u.address || "",
        location: u.Location || u.location || (u.city || u.state ? `${u.city || ""}, ${u.state || ""}`.trim().replace(/^,|,$/, "") : "N/A"),
        registered: u.CreatedAt || u.registered || u.created_at || "",
        manglik: u.Manglik || u.manglik || "",
        assigned_to: getAssignedTo(u),
        tagAllocated: u.tagAllocated || u.TagAllocated || 0,
        photo: u.Photo || u.photo || "",
        version: u.version || u.Version || "1.0",
        totalCredits: u.TotalCredits || u.totalCredits || 0,
        creditsUsed: u.CreditsUsed || u.creditsUsed || 0,
        skippedCount: u.SkippedCount || u.skippedCount || u.skipped_count || 0,
        potentialCount: u.PotentialCount || u.potentialCount || u.potential_count || 0,
        verified: u.verified || u.is_verified || false,
        isPremium: activeTab === "premium" ? true : checkPremium(u),
        premium: u?.premium,
        is_premium: u?.is_premium,
        app_status: u.app_status || "not_installed",
        app_platform: u.app_platform || null,
        app_version: u.app_version || null,
        app_last_seen_at: u.app_last_seen_at || null,
      }));

      setUsersData(mappedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsersData([]);
    } finally {
      LoaderService.hide();
    }
  }, [query, rowsPerPage, profileStatus, filters, activeTab]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query && query.trim().length >= 2) {
        searchUsers(query.trim());
      } else if (!query) {
        fetchUsers(page);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [query, page, searchUsers, fetchUsers, profileStatus, filters, activeTab]);

  const registeredUsers = useMemo(() => usersData, [usersData]);
  const guestUsers = useMemo(() => usersData, [usersData]);
  const premiumUsers = useMemo(() => usersData, [usersData]);

  const activeUsers = activeTab === "registered"
    ? registeredUsers
    : activeTab === "guest"
      ? guestUsers
      : premiumUsers;

  // No need for local filtering since API search handles it
  const sortedUsers = useMemo(() => {
    if (!sortConfig) return activeUsers;
    return [...activeUsers].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [activeUsers, sortConfig]);

  const [statsModal, setStatsModal] = useState({
    show: false,
    userId: null,
    userName: "",
    type: "", // "matches", "queue", "publish"
    data: null,
    loading: false,
  });

  const handleCountClick = useCallback(async (userId, type, userName) => {
    setStatsModal({
      show: true,
      userId,
      userName,
      type,
      data: null,
      loading: true,
    });
    try {
      const response = await GetUserMatchStats(userId);
      if (response?.status && response?.data) {
        setStatsModal(prev => ({
          ...prev,
          data: response.data,
          loading: false,
        }));
      } else {
        toast.error("Failed to load match statistics");
        setStatsModal(prev => ({ ...prev, show: false, loading: false }));
      }
    } catch (err) {
      console.error(err);
      toast.error("Error loading match statistics");
      setStatsModal(prev => ({ ...prev, show: false, loading: false }));
    }
  }, []);

  const [overviewModal, setOverviewModal] = useState({
    show: false,
    userId: null,
    userName: "",
    data: null,
    loading: false,
  });

  const handleOverviewClick = useCallback(async (userId, userName) => {
    setOverviewModal({
      show: true,
      userId,
      userName,
      data: null,
      loading: true,
    });
    try {
      const response = await GetUserMatchStats(userId);
      if (response?.status && response?.data) {
        setOverviewModal(prev => ({
          ...prev,
          data: response.data,
          loading: false,
        }));
      } else {
        toast.error("Failed to load match statistics overview");
        setOverviewModal(prev => ({ ...prev, show: false, loading: false }));
      }
    } catch (err) {
      console.error(err);
      toast.error("Error loading match statistics overview");
      setOverviewModal(prev => ({ ...prev, show: false, loading: false }));
    }
  }, []);

  // Inject state into columns for Service dropdown
  const columnsWithState = useMemo(() => {
    let filteredColumns = columns;
    if (activeTab === "premium") {
      filteredColumns = columns.filter(
        (col) =>
          col.label !== "Completion" &&
          col.label !== "Actions" &&
          col.label !== "Caste" &&
          col.label !== "Verified" &&
          col.label !== "Skipped" &&
          col.label !== "Potential Match"
      );
    } else {
      filteredColumns = columns;
    }
    return filteredColumns.map(col => {
      if (col.accessor === 'name') {
        return {
          ...col,
          cellRenderer: (row) => (
            <div className="flex items-center gap-3">
              {activeTab !== "premium" && (
                <UserAvatar name={row.name} photo={row.photo} />
              )}
             
              <div>
                <div className="flex items-center gap-1.5 leading-none mb-1">
                  <span className="text-sm font-bold text-white">{row.name}</span>
                  {checkPremium(row) && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[9px] font-black uppercase tracking-widest">
                      ⭐ Premium
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                  {activeTab === "premium" ? (row.gender || "N/A") : `${row.gender || "N/A"} • ${row.age || "??"}y`}
                </div>
              </div>
            </div>
          )
        };
      }
      if (col.accessor === 'service') {
        return {
          ...col,
          cellRenderer: (row) => col.cellRenderer(row, handleRunService, handleGenerateAboutMe)
        };
      }
      if (col.accessor === 'verified') {
        return {
          ...col,
          cellRenderer: (row) => col.cellRenderer(row, handleRunService, (user) => setVerifyModal(user))
        };
      }
      if (col.accessor === 'caste') {
        return {
          ...col,
          cellRenderer: (row) => {
            const isEditing = editingCasteUserId === row.id;
            return (
              <div className="relative group min-w-[120px]">
                {isEditing ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={editedCasteValue}
                      onChange={(e) => setEditedCasteValue(e.target.value)}
                      className="w-28 bg-slate-950 border border-blue-500/50 text-[11px] text-white px-1.5 py-0.5 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-lg shadow-blue-500/10"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateCaste(row.id);
                        if (e.key === 'Escape') setEditingCasteUserId(null);
                      }}
                    />
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleUpdateCaste(row.id); }}
                      className="p-1 bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 rounded transition-all"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setEditingCasteUserId(null); }}
                      className="p-1 bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 rounded transition-all"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ) : (
                  <div 
                    className="flex items-center justify-between gap-2 cursor-pointer group/caste p-1 rounded hover:bg-white/5 transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingCasteUserId(row.id);
                      setEditedCasteValue(row.caste || "");
                    }}
                  >
                    <span className="text-xs text-slate-300 font-medium truncate">
                      {row.caste || "N/A"}
                    </span>
                    <button className="opacity-0 group-hover/caste:opacity-100 p-1 text-blue-400 hover:text-blue-300 transition-all">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                  </div>
                )}
              </div>
            );
          }
        };
      }
      if (col.accessor === 'email') {
        return {
          ...col,
          cellRenderer: (row) => {
            const isEditing = editingPhoneUserId === row.id;
            return (
              <div className="space-y-1 relative group">
                {isEditing ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={editedPhoneValue}
                      maxLength={10}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        if (val.length <= 10) setEditedPhoneValue(val);
                      }}
                      className="w-24 bg-slate-950 border border-blue-500/50 text-[11px] text-white px-1.5 py-0.5 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-lg shadow-blue-500/10"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleUpdatePhone(row.id); }}
                      className="p-1 bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 rounded transition-all"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setEditingPhoneUserId(null); }}
                      className="p-1 bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 rounded transition-all"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 group/phone">
                    <div className="text-xs text-slate-300 font-medium">{row.phone}</div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingPhoneUserId(row.id);
                        setEditedPhoneValue(row.phone || "");
                      }}
                      className="opacity-0 group-hover/phone:opacity-100 p-1 bg-slate-800 text-slate-400 hover:text-blue-400 rounded transition-all"
                      title="Update Phone"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                )}
                <div className="text-[10px] text-slate-500 font-bold">{row.email}</div>
              </div>
            ) ;
          }
        };
      }
      if (
        col.accessor === 'matchCount' || 
        col.accessor === 'queueCount' || 
        col.accessor === 'publishCount' ||
        col.accessor === 'likesSentCount' ||
        col.accessor === 'dislikesSentCount' ||
        col.accessor === 'likedByCount'
      ) {
        return {
          ...col,
          cellRenderer: (row) => col.cellRenderer(row, null, null, handleCountClick)
        };
      }
      if (col.accessor === 'overview') {
        return {
          ...col,
          cellRenderer: (row) => col.cellRenderer(row, null, null, null, handleOverviewClick)
        };
      }
      return col;
    });
  }, [handleRunService, handleGenerateAboutMe, editingPhoneUserId, editedPhoneValue, handleUpdatePhone, activeTab, handleCountClick, handleOverviewClick]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return null;
    });
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const Pagination = ({ page, setPage, totalPages }) => {
    const getPageNumbers = () => {
      const pages = [];
      const showEllipsisStart = page > 4;
      const showEllipsisEnd = page < totalPages - 3;
      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        if (showEllipsisStart) pages.push("...");
        let start = Math.max(2, page - 1);
        let end = Math.min(totalPages - 1, page + 1);
        if (!showEllipsisStart) { start = 2; end = 5; }
        if (!showEllipsisEnd) { end = totalPages - 1; start = totalPages - 4; }
        for (let i = start; i <= end; i++) pages.push(i);
        if (showEllipsisEnd) pages.push("...");
        pages.push(totalPages);
      }
      return pages;
    };

    return (
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6 p-4 border-t border-slate-800">
        <div className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
          Registry Results: {totalCount} Records detected
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="h-8 px-3 bg-slate-900 border border-slate-800 text-slate-400 rounded hover:text-white transition-all disabled:opacity-20"
            >
              &lt;
            </button>
            {getPageNumbers().map((p, idx) => (
              <button
                key={idx}
                onClick={() => typeof p === 'number' && setPage(p)}
                disabled={p === "..."}
                className={`h-8 w-8 text-[11px] font-bold rounded transiton-all ${page === p
                  ? "bg-white text-black"
                  : p === "..."
                    ? "bg-transparent text-slate-600"
                    : "bg-slate-900 border border-slate-800 text-slate-400 hover:text-white"
                  }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="h-8 px-3 bg-slate-900 border border-slate-800 text-slate-400 rounded hover:text-white transition-all disabled:opacity-20"
            >
              &gt;
            </button>
          </div>

          <div className="flex items-center gap-3 border-l border-slate-800 pl-4 h-8">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">Limit:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                className="h-7 bg-slate-900 border border-slate-800 text-[10px] font-black text-blue-500 px-2 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500/50 uppercase transition-all cursor-pointer"
              >
                {[10, 20, 50, 100, 500, 1000].map(val => (
                  <option key={val} value={val}>{val}</option>
                ))}
              </select>
            </div>

            <div className="h-4 w-px bg-slate-800 mx-1"></div>

            <div className="flex items-center gap-2">
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">Jump to:</span>
              <select
                value={page}
                onChange={(e) => setPage(Number(e.target.value))}
                className="h-7 bg-slate-900 border border-slate-800 text-[10px] font-black text-blue-500 px-2 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500/50 uppercase transition-all cursor-pointer"
              >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <option key={p} value={p}>Page {p}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-12 space-y-6 animate-fade-in">

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-slate-900/50 p-1 rounded-xl border border-slate-800 w-fit">
        {[
          { key: "registered", label: "Registered Users", count: activeTab === "registered" ? totalCount : "..." },
          { key: "guest", label: "Guest Users", count: activeTab === "guest" ? totalCount : "..." },
          { key: "premium", label: "Premium User", count: activeTab === "premium" ? totalCount : "..." },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key);
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
              activeTab === tab.key
                ? "bg-slate-700 text-white shadow"
                : "text-slate-500 hover:text-slate-300"
            }`}
          >
            {tab.label}
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-black ${
              activeTab === tab.key ? "bg-slate-600 text-slate-200" : "bg-slate-800 text-slate-500"
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* List Actions Cluster */}
      <div className="flex flex-col lg:flex-row justify-between items-center gap-4 bg-slate-900/50 p-4 rounded-2xl border border-slate-800">
        <div className="flex-1 flex items-center gap-3 relative group">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              {isSearching ? (
                <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <span className="opacity-40 group-focus-within:opacity-100 transition-opacity">🔍</span>
              )}
            </div>
            <input
              type="text"
              placeholder="SEARCH BY NAME, PHONE, EMAIL, ID, CITY..."
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              className="w-full bg-slate-950 border border-slate-800 text-white text-[11px] font-bold pl-10 pr-10 py-2.5 rounded-xl focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all uppercase tracking-widest placeholder:text-slate-700"
            />
            {query && (
              <button
                onClick={() => { setQuery(''); setPage(1); }}
                className="absolute inset-y-0 right-3 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {profileStatus && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-xl animate-fade-in whitespace-nowrap">
              <span className="text-[10px] font-black text-blue-400 uppercase tracking-tighter">
                Filtered: {profileStatus}
              </span>
              <button
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('status');
                  navigate({ search: newParams.toString() });
                }}
                className="text-blue-500 hover:text-blue-300 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {premiumStatus && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-xl animate-fade-in whitespace-nowrap">
              <span className="text-[10px] font-black text-emerald-400 uppercase tracking-tighter">
                Premium Users Only
              </span>
              <button
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('premium');
                  navigate({ search: newParams.toString() });
                }}
                className="text-emerald-500 hover:text-emerald-300 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {filters.community && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-500/10 border border-indigo-500/30 rounded-xl animate-fade-in whitespace-nowrap">
              <span className="text-[10px] font-black text-indigo-300 uppercase tracking-tighter">
                Community: {filters.community}
              </span>
              <button
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('community');
                  navigate({ search: newParams.toString() });
                }}
                className="text-indigo-400 hover:text-indigo-200 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchUsers(page)}
            className="h-10 px-4 bg-slate-800 text-slate-300 font-black text-[10px] rounded-lg hover:bg-slate-700 hover:text-white transition-all uppercase tracking-widest border border-slate-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Filters Row */}
      {activeTab !== "premium" && (
        <div className="bg-slate-900/50 rounded-2xl border border-slate-800 transition-all duration-200">
          {/* Header Bar with Toggle Arrow */}
          <div
            className={`flex items-center justify-between px-4 py-2.5 cursor-pointer select-none transition-colors hover:bg-slate-800/30 ${
              !isFiltersMinimized ? "border-b border-slate-800/80" : ""
            }`}
            onClick={() => {
              setIsFiltersMinimized(prev => {
                const next = !prev;
                localStorage.setItem("users_filters_minimized", String(next));
                return next;
              });
            }}
          >
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Filters
                </span>
                {activeFiltersCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    {activeFiltersCount} ACTIVE
                  </span>
                )}
              </div>

              {/* In minimized view, display active filter pills so user sees active filters at a glance */}
              {isFiltersMinimized && hasActiveFilters && (
                <div className="flex items-center gap-1.5 flex-wrap ml-2">
                  {filters.salary && <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded text-[9px] font-bold">Salary: {filters.salary}</span>}
                  {filters.age && <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded text-[9px] font-bold">Age: {filters.age}</span>}
                  {filters.qualification && <span className="px-2 py-0.5 bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 rounded text-[9px] font-bold">Qual: {filters.qualification}</span>}
                  {filters.profession && <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded text-[9px] font-bold">Prof: {filters.profession}</span>}
                  {filters.gender && <span className="px-2 py-0.5 bg-slate-500/10 text-slate-300 border border-slate-500/20 rounded text-[9px] font-bold">Gender: {filters.gender}</span>}
                  {filters.city && <span className="px-2 py-0.5 bg-sky-500/10 text-sky-400 border border-sky-500/20 rounded text-[9px] font-bold">City: {filters.city}</span>}
                  {filters.state && <span className="px-2 py-0.5 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded text-[9px] font-bold">State: {filters.state}</span>}
                  {filters.community && <span className="px-2 py-0.5 bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 rounded text-[9px] font-bold">Community: {filters.community}</span>}
                  {filters.manglik && <span className="px-2 py-0.5 bg-fuchsia-500/10 text-fuchsia-400 border border-fuchsia-500/20 rounded text-[9px] font-bold">Manglik: {filters.manglik}</span>}
                  {filters.marital_status && <span className="px-2 py-0.5 bg-violet-500/10 text-violet-300 border border-violet-500/20 rounded text-[9px] font-bold">Status: {filters.marital_status}</span>}
                  {filters.family_type && <span className="px-2 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded text-[9px] font-bold">Family: {filters.family_type}</span>}
                  {filters.min_height && <span className="px-2 py-0.5 bg-teal-500/10 text-teal-400 border border-teal-500/20 rounded text-[9px] font-bold">Min H: {filters.min_height}</span>}
                  {filters.max_height && <span className="px-2 py-0.5 bg-teal-500/10 text-teal-400 border border-teal-500/20 rounded text-[9px] font-bold">Max H: {filters.max_height}</span>}
                  {filters.family_background && <span className="px-2 py-0.5 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded text-[9px] font-bold">Background: {filters.family_background}</span>}
                  {filters.assigned_to && <span className="px-2 py-0.5 bg-lime-500/10 text-lime-400 border border-lime-500/20 rounded text-[9px] font-bold">Assigned: {filters.assigned_to}</span>}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearAllFilters();
                  }}
                  className="h-7 px-2.5 bg-rose-500/10 text-rose-400 border border-rose-500/30 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-rose-500/20 transition-all flex items-center gap-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear
                </button>
              )}

              {/* Arrow toggle button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFiltersMinimized(prev => {
                    const next = !prev;
                    localStorage.setItem("users_filters_minimized", String(next));
                    return next;
                  });
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white transition-all border border-slate-700 text-[10px] font-bold uppercase tracking-wider"
                title={isFiltersMinimized ? "Expand filters" : "Minimize filters"}
              >
                <span>{isFiltersMinimized ? "Expand" : "Minimize"}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-3.5 w-3.5 transition-transform duration-200 ${isFiltersMinimized ? "" : "rotate-180"}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Collapsible Dropdown Content */}
          {!isFiltersMinimized && (
            <div className="flex flex-wrap items-center gap-3 p-4">
              <select
                value={filters.salary}
                onChange={(e) => { setFilters(prev => ({ ...prev, salary: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All Salary</option>
                {dropdownOptions.salaryOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.age}
                onChange={(e) => { setFilters(prev => ({ ...prev, age: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All Ages</option>
                <option value="18-21">18-21</option>
                <option value="21-25">21-25</option>
                <option value="25-30">25-30</option>
                <option value="30-35">30-35</option>
                <option value="35-40">35-40</option>
                <option value="40-50">40-50</option>
                <option value="50-60">50-60</option>
              </select>

              <select
                value={filters.qualification}
                onChange={(e) => { setFilters(prev => ({ ...prev, qualification: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All Qualifications</option>
                {dropdownOptions.qualificationOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.profession}
                onChange={(e) => { setFilters(prev => ({ ...prev, profession: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All Professions</option>
                {dropdownOptions.professionOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.gender}
                onChange={(e) => { setFilters(prev => ({ ...prev, gender: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All Genders</option>
                {dropdownOptions.genderOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.city}
                onChange={(e) => { setFilters(prev => ({ ...prev, city: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All Cities</option>
                {dropdownOptions.cityOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.state}
                onChange={(e) => { setFilters(prev => ({ ...prev, state: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All States</option>
                {dropdownOptions.stateOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.community}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, community: e.target.value }));
                  setPage(1);
                }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All Communities</option>
                {dropdownOptions.communityOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.manglik}
                onChange={(e) => { setFilters(prev => ({ ...prev, manglik: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All Manglik Status</option>
                {dropdownOptions.manglikOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.marital_status}
                onChange={(e) => { setFilters(prev => ({ ...prev, marital_status: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All Marital Statuses</option>
                {dropdownOptions.maritalStatusOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.family_type}
                onChange={(e) => { setFilters(prev => ({ ...prev, family_type: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All Family Types</option>
                {dropdownOptions.familyTypeOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.min_height}
                onChange={(e) => { setFilters(prev => ({ ...prev, min_height: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">Min Height</option>
                {dropdownOptions.heightOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.max_height}
                onChange={(e) => { setFilters(prev => ({ ...prev, max_height: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">Max Height</option>
                {dropdownOptions.heightOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.family_background}
                onChange={(e) => { setFilters(prev => ({ ...prev, family_background: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All Family Backgrounds</option>
                {dropdownOptions.familyBackgroundOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              <select
                value={filters.assigned_to}
                onChange={(e) => { setFilters(prev => ({ ...prev, assigned_to: e.target.value })); setPage(1); }}
                className="h-9 bg-slate-950 border border-slate-800 text-[11px] font-bold text-slate-300 px-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all cursor-pointer uppercase tracking-wider"
              >
                <option value="">All Assignments</option>
                <option value="unassigned">Unassigned</option>
                {dropdownOptions.assignedToOptions.map((username) => (
                  <option key={username} value={username}>{username}</option>
                ))}
              </select>

              {hasActiveFilters && (
                <button
                  onClick={handleClearAllFilters}
                  className="h-9 px-3 bg-rose-500/10 text-rose-400 border border-rose-500/30 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-rose-500/20 transition-all flex items-center gap-1.5"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear Filters
                </button>
              )}

              {hasActiveFilters && (
                <div className="flex items-center gap-2 ml-auto flex-wrap">
                  {filters.salary && (
                    <span className="px-2.5 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      Salary: {filters.salary}
                    </span>
                  )}
                  {filters.age && (
                    <span className="px-2.5 py-1 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      Age: {filters.age}
                    </span>
                  )}
                  {filters.qualification && (
                    <span className="px-2.5 py-1 bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      Qualification: {filters.qualification}
                    </span>
                  )}
                  {filters.profession && (
                    <span className="px-2.5 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      Profession: {filters.profession}
                    </span>
                  )}
                  {filters.gender && (
                    <span className="px-2.5 py-1 bg-slate-500/10 text-slate-300 border border-slate-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      Gender: {filters.gender}
                    </span>
                  )}
                  {filters.city && (
                    <span className="px-2.5 py-1 bg-sky-500/10 text-sky-400 border border-sky-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      City: {filters.city}
                    </span>
                  )}
                  {filters.state && (
                    <span className="px-2.5 py-1 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      State: {filters.state}
                    </span>
                  )}
                  {filters.community && (
                    <span className="px-2.5 py-1 bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      Community: {filters.community}
                    </span>
                  )}
                  {filters.manglik && (
                    <span className="px-2.5 py-1 bg-fuchsia-500/10 text-fuchsia-400 border border-fuchsia-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      Manglik: {filters.manglik}
                    </span>
                  )}
                  {filters.marital_status && (
                    <span className="px-2.5 py-1 bg-violet-500/10 text-violet-300 border border-violet-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      Marital Status: {filters.marital_status}
                    </span>
                  )}
                  {filters.family_type && (
                    <span className="px-2.5 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      Family Type: {filters.family_type}
                    </span>
                  )}
                  {filters.family_background && (
                    <span className="px-2.5 py-1 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      Family Background: {filters.family_background}
                    </span>
                  )}
                  {filters.premium && (
                    <span className="px-2.5 py-1 bg-amber-500/10 text-amber-500 border border-amber-500/20 rounded-lg text-[10px] font-black uppercase tracking-tighter">
                      Premium: {filters.premium}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="rounded-3xl border border-slate-800 shadow-2xl shadow-black/40 flex flex-col bg-slate-900/50" style={{ height: isFiltersMinimized ? 'calc(100vh - 210px)' : 'calc(100vh - 280px)' }}>
        <div className="overflow-x-auto overflow-y-auto flex-1 custom-scrollbar rounded-t-3xl">
          <TableCardView
            data={sortedUsers}
            columns={columnsWithState}
            view={view}
            sortConfig={sortConfig}
            onSort={handleSort}
            classtype="users"
          />
        </div>
        <div className="border-t border-slate-800 bg-slate-900/80 backdrop-blur-sm flex-shrink-0">
          <Pagination page={page} setPage={setPage} totalPages={totalPages} />
        </div>
      </div>

      {verifyModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4" onClick={() => setVerifyModal(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-black text-white uppercase tracking-tight">Verify User</h3>
            </div>
            <p className="text-slate-400 text-sm mb-6">
              Are you sure you want to verify <span className="text-white font-bold">{verifyModal.name}</span> (ID: {verifyModal.id})?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setVerifyModal(null)}
                className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-all font-bold text-sm uppercase tracking-wider"
              >
                Cancel
              </button>
              <button
                onClick={() => handleVerifyUser(verifyModal.id)}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-all font-bold text-sm uppercase tracking-wider shadow-lg shadow-blue-600/20"
              >
                Verify
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <SimpleMessageModal
        show={errorModal.show}
        onClose={() => setErrorModal(prev => ({ ...prev, show: false }))}
        message={errorModal.message}
        variant={errorModal.variant}
      />

      <MatchStatsModal
        show={statsModal.show}
        onClose={() => setStatsModal(prev => ({ ...prev, show: false }))}
        type={statsModal.type}
        userName={statsModal.userName}
        data={statsModal.data}
        loading={statsModal.loading}
      />

      <MatchStatsOverviewModal
        show={overviewModal.show}
        onClose={() => setOverviewModal(prev => ({ ...prev, show: false }))}
        userName={overviewModal.userName}
        data={overviewModal.data}
        loading={overviewModal.loading}
      />
    </div>
  );
};

export default Users;
